import { setQueriesDataFromKey } from '@pba/react-query';
import type { ChatroomDataObject, MessageDataObject, MessageViewDataObject } from '#models/services';
import { useQueryClient } from '#react-query';
import { type ChatroomsQueryData, getChatroomDetailQueryKey } from '../use-get-chatroom';
import { type ChatroomsInfiniteQueryData, getChatroomsInfiniteQueryKey } from '../use-get-chatrooms';

const applyNewMessageToChatroom = ({
  chatroom,
  newMessage,
  unreadCountDifference = 0,
  unseenCountDifference = 0,
}: {
  chatroom: ChatroomDataObject;
  newMessage: MessageViewDataObject;
  unreadCountDifference?: number;
  unseenCountDifference?: number;
}) => ({
  ...chatroom,
  message: newMessage,
  updatedAt: newMessage.updatedAt,
  ...(chatroom.stats && {
    stats: {
      ...chatroom.stats,
      unread: chatroom.stats.unread + unreadCountDifference,
      unseen: (chatroom.stats.unseen ?? 0) + unseenCountDifference,
    },
  }),
});

const updateChatroomsInfiniteQueryData = ({
  chatroomId,
  data,
  newMessage,
  unreadCountDifference = 0,
  unseenCountDifference = 0,
}: {
  chatroomId: string;
  data: ChatroomsInfiniteQueryData['data'];
  newMessage: MessageViewDataObject;
  unreadCountDifference?: number;
  unseenCountDifference?: number;
}) => {
  const existingChatroom = data.pages.flat().find((chatroom) => chatroom?._id === chatroomId);

  if (!existingChatroom) {
    return null;
  }

  const updatedChatroom = applyNewMessageToChatroom({
    chatroom: existingChatroom,
    newMessage,
    unreadCountDifference,
    unseenCountDifference,
  });

  const pages = data.pages.map((page) => page.map((chatroom) => (chatroom?._id === chatroomId ? null : chatroom)));

  return {
    ...data,
    pages: [[updatedChatroom, ...(pages[0] ?? [])], ...pages.slice(1)],
  };
};

type P = {
  chatroomId: string;
  newMessage: MessageDataObject;
  unreadCountDifference?: number;
  unseenCountDifference?: number;
};

export const useUpdateChatroomsCache = () => {
  const queryClient = useQueryClient();

  return ({ chatroomId, newMessage, unreadCountDifference = 0, unseenCountDifference = 0 }: P) => {
    setQueriesDataFromKey<ChatroomsQueryData>(
      queryClient,
      getChatroomDetailQueryKey({ id: chatroomId }),
      (detailChatroom) =>
        detailChatroom
          ? applyNewMessageToChatroom({
              chatroom: detailChatroom,
              newMessage,
              unreadCountDifference,
              unseenCountDifference,
            })
          : detailChatroom,
    );

    const chatroomsInfiniteQueries = queryClient.getQueryCache().findAll({
      queryKey: getChatroomsInfiniteQueryKey({}),
    });

    for (const query of chatroomsInfiniteQueries) {
      const data = queryClient.getQueryData<ChatroomsInfiniteQueryData['data']>(query.queryKey);

      if (!data) {
        continue;
      }

      const updatedData = updateChatroomsInfiniteQueryData({
        chatroomId,
        data,
        newMessage,
        unreadCountDifference,
        unseenCountDifference,
      });

      if (!updatedData) {
        continue;
      }

      queryClient.setQueryData<ChatroomsInfiniteQueryData['data']>(query.queryKey, updatedData);
    }
  };
};
